// The caller's in-app feed: their lines in the organization, newest first, each saying what it is
// about as that stands now and which screen it opens; how many are unread; and reading one line, or
// all of them. Any member, for themselves.
//
// A line is listed while the mail would still tell it — its subject live, the caller still allowed
// to be told of it and their `in_app` choice for its kind still on — so a line can leave the feed
// between two reads, and come back.

import { api, withQuery } from '../client'
import type { components, operations } from '../schema'

export type NotificationEntry = components['schemas']['NotificationEntry']
export type NotificationUpload = components['schemas']['NotificationUpload']
export type NotificationMembershipRequest = components['schemas']['NotificationMembershipRequest']
export type NotificationWaitingRequests = components['schemas']['NotificationWaitingRequests']
export type NotificationSale = components['schemas']['NotificationSale']
export type NotificationContactMessage = components['schemas']['NotificationContactMessage']
export type NotificationFeedPage = components['schemas']['Page_NotificationEntry']
export type NotificationCount = components['schemas']['NotificationCount']
/** The walk: `unread`, the lines not read yet, and base's `limit` and `after`; always newest first. */
export type NotificationFeedQuery = NonNullable<operations['orgs_notifications_list']['parameters']['query']>
/** The narrowing a count takes: the listing's `unread`. */
export type NotificationCountQuery = NonNullable<operations['orgs_notifications_count']['parameters']['query']>

const FEED = '/orgs/current/notifications'

/** One page of the caller's lines, newest first; `next` is the cursor of the following page, or `null`. */
export function listNotifications(
  query: NotificationFeedQuery = {},
  signal?: AbortSignal,
): Promise<NotificationFeedPage> {
  return api.get<NotificationFeedPage>(withQuery(FEED, query), undefined, signal)
}

/** How many lines the feed would list with the same `unread` — the bell's badge asks it with `unread: true`. */
export function countNotifications(
  query: NotificationCountQuery = {},
  signal?: AbortSignal,
): Promise<NotificationCount> {
  return api.get<NotificationCount>(withQuery(`${FEED}/count`, query), undefined, signal)
}

/** Mark one line read. Idempotent: a line already read keeps its first instant. 404 for a line not the caller's. */
export function markNotificationRead(itemId: string): Promise<void> {
  return api.post<void>(`${FEED}/${encodeURIComponent(itemId)}/read`)
}

/** Mark every unread line of the caller's feed in this organization read. Idempotent. */
export function markAllNotificationsRead(): Promise<void> {
  return api.post<void>(`${FEED}/read-all`)
}
